import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { CompanyResearchLoadingNotice } from "../components/CompanyResearchLoadingNotice";
import type {
  CompanyResearchRecordList,
  CompanyResearchRecordListItem,
} from "../lib/companyResearch";
import { companyResearchClient } from "../lib/companyResearchClient";

function createErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) return error.message;
  return "調査履歴の取得に失敗しました。時間をおいて再試行してください。";
}

function getRecordItems(list: CompanyResearchRecordList) {
  return list.records ?? list.items ?? [];
}

function formatDate(value?: string) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function createExcerpt(value?: string) {
  if (!value) return "";
  const plain = value
    .replace(/^#+\s*/gm, "")
    .replace(/[-*]\s+/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return plain.length > 140 ? `${plain.slice(0, 140)}...` : plain;
}

export default function RequestCompanyResearchJobListPage() {
  const [records, setRecords] = useState<CompanyResearchRecordListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    (async () => {
      setLoading(true);
      try {
        const list = await companyResearchClient.listRecords();
        if (!active) return;
        setRecords(getRecordItems(list));
        setErrorMessage(null);
      } catch (error) {
        if (!active) return;
        setErrorMessage(createErrorMessage(error));
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-5xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/request/company-research" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ← 企業リサーチへ
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">企業調査履歴</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="rounded-[2rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-black tracking-[0.12em] text-[#A67C52]">企業リサーチ</p>
              <h1 className="mt-3 text-2xl font-black tracking-tight text-[#7C5A4E]">企業調査履歴</h1>
            </div>
            <Link
              to="/request/company-research"
              state={{ startNew: true }}
              className="inline-flex min-h-[40px] items-center justify-center rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#5D4D46]"
            >
              新規リサーチ
            </Link>
          </div>
          {errorMessage && <p className="mt-4 rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">{errorMessage}</p>}
        </section>

        <section
          aria-label="企業リサーチ一覧"
          className="mt-5 rounded-[2rem] border border-[#EADBC8] bg-white/80 p-4 shadow-[0_18px_48px_rgba(124,90,78,0.12)] sm:p-5"
        >
          {loading ? (
            <CompanyResearchLoadingNotice title="調査履歴を読み込んでいます。" />
          ) : records.length === 0 ? (
            <div className="flex min-h-[280px] items-center justify-center rounded-[1.5rem] border border-dashed border-[#EADBC8] bg-[#FFFCFA] p-5 text-center text-sm leading-7 text-[#6B5851]">
              まだ企業調査履歴はありません。
            </div>
          ) : (
            <div className="grid gap-3">
              {records.map((record) => (
                <article key={record.quick_summary_id} className="overflow-hidden rounded-2xl border border-[#EADBC8] bg-white p-4 shadow-sm">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <h2 className="break-words text-base font-black leading-7 text-[#5D4D46] [overflow-wrap:anywhere]">
                        {record.company_name || "企業名未設定"}
                      </h2>
                      {record.company_url && (
                        <a
                          href={record.company_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 block break-all text-sm font-bold text-[#A67C52] underline underline-offset-2 [overflow-wrap:anywhere]"
                        >
                          {record.company_url}
                        </a>
                      )}
                      {createExcerpt(record.display_text) && (
                        <p className="mt-2 break-words text-sm leading-6 text-[#6B5851] [overflow-wrap:anywhere]">
                          <span className="font-black text-[#5D4D46]">概要抜粋: </span>
                          {createExcerpt(record.display_text)}
                        </p>
                      )}
                      <p className="mt-2 text-xs font-bold text-[#9C867D]">作成: {formatDate(record.created_at)}</p>
                    </div>
                    <Link
                      to={`/request/company-research/jobs/${encodeURIComponent(record.quick_summary_id)}`}
                      className="min-h-[42px] w-full shrink-0 rounded-full bg-[#7C5A4E] px-4 py-2 text-center text-sm font-black text-white shadow-sm transition hover:bg-[#5D4D46] sm:w-auto"
                    >
                      詳細を見る
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
